import { Component, OnInit } from '@angular/core';
import { AdoptionStageComponent } from '../../../../core/layout/components/adoption-stage/adoption-stage.component';
import { OrganizationChartComponent } from '../../../../core/layout/components/organization-chart/organization-chart.component';
import { ActivatedRoute, Router } from '@angular/router';
import { IOrganizationNode } from '../../../../core/layout/components/organization-chart/models/organization-chart.types';
import { PrepareService } from '../prepare/services/prepare.service';
import { CommonModule } from '@angular/common';
import { LoadingComponent } from '../../../../core/layout/components/loading/loading.component';
import { ToolbarComponent } from '../../../../core/layout/components/toolbar/toolbar.component';
import { CredibilityComponent } from '../../../../core/layout/components/credibility/credibility.component';
import { UsefulWeeksComponent } from '../../../../core/layout/components/useful-weeks/useful-weeks.component';
import { CriticalMassComponent } from '../../../../core/layout/components/critical-mass/critical-mass.component';
import { DecideService } from './services/decide.service';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import {
  IDecision,
  IGetProgressResponse,
  IProgressOrganizationChart,
  IProgressUsefulWeeks,
} from './model/decide.model';
import { StagesComponent } from '../../../../core/layout/components/stages/stages.component';
import { AuthService } from '../../../../core/auth/services/auth.service';
import { lastValueFrom } from 'rxjs';
import {
  ActionModalComponent,
  ActionModalData,
} from './components/action-modal/action-modal.component';
import { GameService } from '../../../../shared/services/game.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  ConfirmationDialogComponent,
  ConfirmationDialogData,
} from '../../../../shared/components/confirmation-dialog/confirmation-dialog.component';

@Component({
  selector: 'app-decide',
  standalone: true,
  imports: [
    CommonModule,
    OrganizationChartComponent,
    AdoptionStageComponent,
    LoadingComponent,
    ToolbarComponent,
    CredibilityComponent,
    UsefulWeeksComponent,
    CriticalMassComponent,
    MatIconModule,
    MatTooltipModule,
    StagesComponent,
  ],
  templateUrl: './decide.component.html',
  styleUrls: ['./decide.component.scss'],
})
export class DecideComponent implements OnInit {
  prid = '';
  scenarioId = ''; // Parent component variable
  isLoading = true; // Parent component variable

  // DECISIONS LIST VARS
  decisions: IDecision[] = []; // Parent component variable
  selectedDecision?: IDecision; // Parent component variable

  // ORGANIZATION CHART VARS
  // 🔥 COMES FROM CHILD: Updated by OrganizationChartComponent via nodeSelectionChange event
  checkedNodes: IOrganizationNode[] = [];
  organizationChart?: IOrganizationNode; // Parent component variable (used as @Input to child)
  progressOrgChart: IProgressOrganizationChart[] | null = [];
  scale = 0.4;
  zoom = 0.5; // Parent component variable

  // USEFUL WEEKS VARS
  progressUsefulWeeks: IProgressUsefulWeeks = {
    weeksScore: 0,
    weeksArray: [],
  };

  // CRITICAL MASS
  progressCriticalMass = 0;

  // CREDIBILITY
  progressCredibility = 0;

  // ADOPTION STAGE VARS
  progressAdoptionStageOfOrganizationInString = '';
  progressAdoptionStageOfOrganizationInNumber = 0;
  adoptionStagesOfOrganizationLevels: any = {
    Preparation: 1,
    Implementation: 2,
    Support: 3,
  };

  // NEWS OF THE WEEK
  newsOftheWeek: ActionModalData = {
    news: '',
    credibility: 0,
    credibility_delta: 0,
    is_adoption_promoted: false,
    adoption_stage: '',
  };
  // Cambios de nivel detectados después de un refresh
  levelChanges: {
    sk_user: string;
    oldLevel: string | null;
    newLevel: string | null;
    label?: string;
  }[] = [];
  // Agrupación por nuevo nivel
  groupedLevelChanges: Record<
    string,
    {
      sk_user: string;
      oldLevel: string | null;
      newLevel: string | null;
      label?: string;
    }[]
  > = {};

  constructor(
    readonly _prepareService: PrepareService,
    readonly _decideService: DecideService,
    readonly router: Router,
    readonly route: ActivatedRoute,
    readonly authService: AuthService,
    readonly _gameService: GameService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog
  ) {}

  async ngOnInit() {
    this.clearSelection();
    this.getScenarioId(); // sync
    try {
      await this.getPrid();
      this.isLoading = true;
      await Promise.all([
        this.getOrganizationChart(false),
        this.getListOfDecisions(false),
        this.getProgressOrganizationChartAndAdoptionStageAndCriticalMass(false),
        this.getCredibility(false),
        this.getProgressUsefulWeeks(false),
      ]);
      this.isGameOver();
    } catch (error) {
      console.error('Error during component initialization:', error);
    } finally {
      this.isLoading = false;
      this.clearSelection();
    }
  }

  async getOrganizationChart(manageLoading: boolean = true): Promise<void> {
    try {
      if (manageLoading) this.isLoading = true;
      const response = await this._prepareService.getOrganizationChart(
        this.scenarioId
      );
      this.organizationChart = response.organization_chart; // Parent component variable
    } catch (error) {
      console.error('Error on getOrganizationChart' + error);
    } finally {
      if (manageLoading) this.isLoading = false;
    }
  }

  async getListOfDecisions(manageLoading: boolean = true): Promise<void> {
    try {
      if (manageLoading) this.isLoading = true;
      this.decisions = await this._decideService.getListOfDecisions(
        this.scenarioId
      );
    } catch (error) {
      console.error('Error on getListOfDecisions' + error);
    } finally {
      if (manageLoading) this.isLoading = false;
    }
  }

  async getProgressOrganizationChartAndAdoptionStageAndCriticalMass(
    manageLoading: boolean = true
  ): Promise<void> {
    try {
      if (manageLoading) this.isLoading = true;
      const progressResponse = await this._decideService.getProgress(
        this.prid,
        this.scenarioId
      );

      this.setProgress(progressResponse);
    } catch (error) {
      console.error('Error on getProgressOrganizationChart' + error);
    } finally {
      if (manageLoading) this.isLoading = false;
    }
  }

  async takeAction(): Promise<void> {
    if (!this.selectedDecision) return;
    // Progress Snapshot (levels) before refresh
    const previousProgressSnapshot: IProgressOrganizationChart[] = (
      this.progressOrgChart || []
    ).map((p) => ({ ...p }));
    try {
      this.isLoading = true;

      if (this.canTakeAction()) {
        const responseTakeAction =
          await this._decideService.saveProgresOrgChart(
            this.selectedDecision,
            this.checkedNodes,
            this.prid,
            this.scenarioId
          );

        this.newsOftheWeek = responseTakeAction;
        this.addWeeksToProgress();

        await this._decideService.saveUsefulWeeks(
          this.prid,
          this.scenarioId,
          this.progressUsefulWeeks
        );
      }
    } catch (error) {
      console.error('Error taking action on takeAction', error);
    } finally {
      await this.resetValues();
      this.isGameOver();
      // Compute level changes
      this.levelChanges = this.computeLevelChanges(
        previousProgressSnapshot,
        this.progressOrgChart || []
      );
      if (this.levelChanges.length) {
        this.groupedLevelChanges = this.groupLevelChangesByNewLevel(
          this.levelChanges
        );
      } else {
        this.groupedLevelChanges = {};
        console.log('No level changes detected.');
      }

      this.showNewsOfTheWeek();
    }
  }

  async getProgressUsefulWeeks(manageLoading: boolean = true): Promise<void> {
    try {
      if (manageLoading) this.isLoading = true;
      this.progressUsefulWeeks =
        await this._decideService.getProgressUsefulWeeks(
          this.prid,
          this.scenarioId
        );
    } catch (error) {
      console.error('Error on getProgressUsefulWeeks' + error);
    } finally {
      if (manageLoading) this.isLoading = false;
    }
  }

  async getCredibility(manageLoading: boolean = true): Promise<void> {
    try {
      if (manageLoading) this.isLoading = true;
      this.progressCredibility = await this._decideService.getCredibility(
        this.prid,
        this.scenarioId
      );
    } catch (error) {
      console.error('Error on getCredibility' + error);
    } finally {
      if (manageLoading) this.isLoading = false;
    }
  }

  setProgress(progressResponse: IGetProgressResponse | null): void {
    if (!progressResponse) return;
    // SET CRITICAL MASS VALUE
    this.progressCriticalMass = progressResponse.critical_mass;

    // SET PROGRESS ORG CHART VALUE
    this.progressOrgChart = progressResponse.org_chart;

    // SET PROGRESS ADOPTION STAGE VALUE
    this.progressAdoptionStageOfOrganizationInString =
      progressResponse.stage_of_the_organization;

    this.progressAdoptionStageOfOrganizationInNumber =
      this.adoptionStagesOfOrganizationLevels[
        this.progressAdoptionStageOfOrganizationInString
      ];
  }

  canTakeAction(): boolean {
    if (!this.selectedDecision) return false;

    const isWholeOrganization =
      this.selectedDecision.scope === 'Whole organization';
    const hasCorrectScope =
      Number(this.selectedDecision.scope) == this.checkedNodes.length;

    return isWholeOrganization || hasCorrectScope;
  }

  getScenarioId(): void {
    this.scenarioId = this.route.snapshot.paramMap.get('id')!; // Parent component variable
    sessionStorage.setItem('scenarioId', this.scenarioId);
  }

  zoomIn(): void {
    this.scale = Math.min(this.scale + 0.1, 2); // max zoom
  }

  zoomOut(): void {
    this.scale = Math.max(this.scale - 0.1, 0.2); // min zoom
  }

  clearSelection() {
    // Clear radio button selection
    this.selectedDecision = undefined; // Parent component variable

    // Clear organization chart selections
    this.clearOrganizationChartSelections();

    // 🔥 RESET CHILD DATA: Clear data that comes from child component
    this.checkedNodes = [];
  }

  clearOrganizationChartSelections(): void {
    if (this.organizationChart) {
      this.setAllNodesSelected(this.organizationChart, false); // Parent component variable
    }
  }

  // Helper method to recursively set all nodes as selected/unselected
  setAllNodesSelected(node: IOrganizationNode, selected: boolean): void {
    node.selected = selected;

    if (node.children) {
      for (const child of node.children) {
        this.setAllNodesSelected(child, selected);
      }
    }
  }

  onDecisionChange(decision: IDecision): void {
    this.clearSelection();
    this.selectedDecision = decision; // Parent component variable
  }

  // 🔥 CHILD EVENT HANDLER: This method receives data from OrganizationChartComponent
  onNodeSelectionChange(nodeOrg: IOrganizationNode[]): void {
    this.checkedNodes = nodeOrg; // Updates parent variable with child data
  }

  showTooltip(): string {
    if (!this.selectedDecision) {
      return 'Please select a decision first';
    }

    if (this.selectedDecision?.scope == 'Whole organization') {
      return '';
    }

    if (
      this.selectedDecision &&
      Number(this.selectedDecision?.scope) != this.checkedNodes.length
    ) {
      return `Please select a minimum of ${this.selectedDecision?.scope} members to take action`;
    }

    return '';
  }

  addWeeksToProgress(): void {
    if (!this.selectedDecision) {
      return;
    }
    const newScore =
      this.progressUsefulWeeks.weeksScore +
      this.selectedDecision.weeks_for_implementation;

    // Filter out any existing entry with the same sk
    const filteredWeeksArray = this.progressUsefulWeeks.weeksArray.filter(
      (item) => item.sk !== this.selectedDecision!.sk
    );

    const blockUntil =
      this.progressUsefulWeeks.weeksArray.length == 0
        ? this.selectedDecision.weeks_until_reuse +
          this.selectedDecision.weeks_for_implementation
        : this.selectedDecision.weeks_until_reuse + newScore;

    this.progressUsefulWeeks = {
      weeksScore: newScore,
      weeksArray: [
        ...filteredWeeksArray,
        {
          sk: this.selectedDecision.sk,
          blockUntil,
        },
      ],
    };
  }

  isDecisionDisabled(decision: IDecision): boolean {
    return this.progressUsefulWeeks.weeksArray.some((item) => {
      return (
        decision.sk === item.sk &&
        this.progressUsefulWeeks.weeksScore < item.blockUntil
      );
    });
  }

  isValidToTakeAction(): boolean {
    if (!this.selectedDecision) {
      return false;
    }

    if (this.selectedDecision?.scope === 'Whole organization') {
      return true;
    }

    if (
      this.selectedDecision &&
      Number(this.selectedDecision?.scope) != this.checkedNodes.length
    ) {
      return false;
    }

    return true;
  }

  isGameOver() {
    const state = {
      adoptionStage: this.progressAdoptionStageOfOrganizationInNumber,
      usefulWeeks: this.progressUsefulWeeks.weeksScore,
      criticalMass: this.progressCriticalMass,
      credibility: this.progressCredibility,
      scenarioId: this.scenarioId,
      prid: this.prid,
    };
    if (this.progressAdoptionStageOfOrganizationInNumber == 3) {
      this.router.navigate([`/game-over/win/${this.scenarioId}`], {
        state,
      });
    } else if (this.progressUsefulWeeks.weeksScore >= 80) {
      this.router.navigate([`/game-over/lose/${this.scenarioId}`], {
        state,
      });
    }
  }

  async showNewsOfTheWeek() {
    // Show modal first
    const modalData: ActionModalData = {
      ...this.newsOftheWeek,
      groupedLevelChanges: this.groupedLevelChanges as any,
    };

    const dialogRef = this.dialog.open(ActionModalComponent, {
      data: modalData,
      disableClose: true,
      maxWidth: '70vw',
    });

    const result = await lastValueFrom(dialogRef.afterClosed());

    if (!result) return; // User cancelled or closed modal
  }

  async getPrid(): Promise<void> {
    try {
      const prid: any = await lastValueFrom(
        this.authService.getUserPridBySession()
      );
      if (prid && typeof prid === 'string') {
        const parts = prid.split('_')[1]?.split('@')[0];
        this.prid = parts || prid;
      } else {
        this.prid = prid;
      }
    } catch (error) {
      console.error('Error on getPrid' + error);
    }
  }

  // Devuelve lista de nodos cuyo level cambió (o apareció nuevo) entre snapshots
  computeLevelChanges(
    previous: IProgressOrganizationChart[],
    current: IProgressOrganizationChart[]
  ): {
    sk_user: string;
    oldLevel: string | null;
    newLevel: string | null;
    label?: string;
  }[] {
    const prevMap = new Map<string, IProgressOrganizationChart>();
    previous.forEach((p) => prevMap.set(p.sk_user, p));

    const changes: {
      sk_user: string;
      oldLevel: string | null;
      newLevel: string | null;
      label?: string;
    }[] = [];

    // Para lookup del label (nombre) desde el árbol
    const labelMap = this.buildLabelMap(this.organizationChart);

    current.forEach((curr) => {
      const prev = prevMap.get(curr.sk_user);
      if (!prev) {
        changes.push({
          sk_user: curr.sk_user,
          oldLevel: null,
          newLevel: curr.level,
          label: labelMap.get(curr.sk_user),
        });
      } else if (prev.level !== curr.level) {
        changes.push({
          sk_user: curr.sk_user,
          oldLevel: prev.level,
          newLevel: curr.level,
          label: labelMap.get(curr.sk_user),
        });
      }
      // Removemos procesados para poder detectar si alguien desapareció (si interesa)
      prevMap.delete(curr.sk_user);
    });
    return changes;
  }

  // Construye un mapa sk_user -> label para facilitar logging
  buildLabelMap(root?: IOrganizationNode): Map<string, string> {
    const map = new Map<string, string>();
    if (!root) return map;
    const walk = (node: IOrganizationNode) => {
      if (node.sk_user) {
        map.set(node.sk_user, node.label || node.title || '');
      }
      if (node.children) node.children.forEach(walk);
    };
    walk(root);
    return map;
  }

  // Agrupa cambios por el valor de newLevel
  groupLevelChangesByNewLevel(
    changes: {
      sk_user: string;
      oldLevel: string | null;
      newLevel: string | null;
      label?: string;
    }[]
  ): Record<
    string,
    {
      sk_user: string;
      oldLevel: string | null;
      newLevel: string | null;
      label?: string;
    }[]
  > {
    return changes.reduce((acc, change) => {
      const key = change.newLevel || 'SIN_NIVEL';
      if (!acc[key]) acc[key] = [];
      acc[key].push(change);
      return acc;
    }, {} as Record<string, { sk_user: string; oldLevel: string | null; newLevel: string | null; label?: string }[]>);
  }

  async startOver() {
    // Show confirmation dialog
    const dialogData: ConfirmationDialogData = {
      title: 'Restart Simulation',
      message:
        'Are you sure you want to restart the simulation? This action cannot be undone and you will lose all current progress.',
      confirmText: 'Yes, restart',
      cancelText: 'Cancel',
      icon: 'restart_alt',
      isDangerous: true,
    };

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: dialogData,
      disableClose: true,
    });

    const confirmed = await lastValueFrom(dialogRef.afterClosed());

    if (!confirmed) {
      return; // User cancelled
    }

    try {
      const response = await this._gameService.startOver(
        this.prid,
        this.scenarioId
      );

      if (response && response.success) {
        // SUCCESS TOAST
        this.snackBar.open(
          `The scenario ${this.scenarioId} has been reset for user ${this.prid} successfully`,
          'Close',
          {
            duration: 5000,
            panelClass: ['success-toast'],
          }
        );

        this.resetValues();
      } else {
        // ERROR TOAST
        this.snackBar.open(
          'Failed to reset the simulator. Please try again.',
          'Close',
          {
            duration: 5000,
            panelClass: ['error-toast'],
          }
        );
      }
    } catch (error) {
      console.error('Error in startOver:', error);
      // ERROR TOAST
      this.snackBar.open(
        'Failed to reset the simulator. Please try again.',
        'Close',
        {
          duration: 5000,
          panelClass: ['error-toast'],
        }
      );
    }
  }

  async resetValues() {
    this.isLoading = false;
    this.clearSelection();
    await Promise.all([
      this.getProgressOrganizationChartAndAdoptionStageAndCriticalMass(false),
      this.getCredibility(false),
      this.getProgressUsefulWeeks(false),
    ]);
  }
}
